import Phaser from 'phaser';
import { GridCell, Wall } from '../types';
import { MazeGrid } from './MazeGrid';
import { findPath } from '../utils/pathfinder';
import { REMNANT_TWEEN_DURATION, CELL_SIZE, COLORS, REMNANT_RECALC_INTERVAL } from '../utils/constants';

const WRAITH_TEX_KEY = '__wraith';

function ensureWraithTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(WRAITH_TEX_KEY)) return;

  const s = Math.round(CELL_SIZE * 0.9); // texture size
  const g = scene.add.graphics();
  const cx = s / 2;
  const headR = s * 0.38; // head radius
  const headY = s * 0.35;

  // Body + head silhouette
  g.fillStyle(0xff4444, 1);
  // Head (circle)
  g.fillCircle(cx, headY, headR);
  // Body (trapezoid from head down)
  g.fillRect(cx - headR, headY, headR * 2, s * 0.4);

  // Wavy bottom tendrils
  const bodyBottom = headY + s * 0.4;
  const tendrilH = s * 0.22;
  const numTendrils = 4;
  const tendrilW = (headR * 2) / numTendrils;
  for (let i = 0; i < numTendrils; i++) {
    const tx = cx - headR + i * tendrilW;
    g.fillTriangle(
      tx, bodyBottom,
      tx + tendrilW, bodyBottom,
      tx + tendrilW / 2, bodyBottom + tendrilH * (i % 2 === 0 ? 1 : 0.65),
    );
  }

  // Eyes — dark with bright pupils
  const eyeSpacing = headR * 0.45;
  const eyeY = headY - headR * 0.05;
  const eyeR = headR * 0.22;
  // Dark sockets
  g.fillStyle(0x000000, 1);
  g.fillCircle(cx - eyeSpacing, eyeY, eyeR);
  g.fillCircle(cx + eyeSpacing, eyeY, eyeR);
  // Bright pupils
  g.fillStyle(0xffcccc, 1);
  g.fillCircle(cx - eyeSpacing, eyeY, eyeR * 0.5);
  g.fillCircle(cx + eyeSpacing, eyeY, eyeR * 0.5);

  g.generateTexture(WRAITH_TEX_KEY, s, s);
  g.destroy();
}

export class Remnant {
  readonly id: number;
  readonly sprite: Phaser.GameObjects.Sprite;
  gridRow: number;
  gridCol: number;
  isMoving: boolean = false;
  path: GridCell[] = [];
  pathIndex: number = 0;
  needsRecalc: boolean = true;

  private scene: Phaser.Scene;
  private maze: MazeGrid;
  private frameCount: number = 0;
  private tweenDuration: number;

  constructor(scene: Phaser.Scene, maze: MazeGrid, id: number, startRow: number, startCol: number, tweenDuration?: number) {
    this.tweenDuration = tweenDuration ?? REMNANT_TWEEN_DURATION;
    this.scene = scene;
    this.maze = maze;
    this.id = id;
    this.gridRow = startRow;
    this.gridCol = startCol;

    const pos = maze.cellToPixel(startRow, startCol);
    ensureWraithTexture(scene);
    this.sprite = scene.add.sprite(pos.x, pos.y, WRAITH_TEX_KEY);
    this.sprite.setDepth(9);
    this.sprite.setScale(0.75);
  }

  update(playerRow: number, playerCol: number): void {
    this.frameCount++;

    // Staggered recalculation
    const shouldRecalc =
      this.needsRecalc ||
      (this.frameCount + this.id * 5) % REMNANT_RECALC_INTERVAL === 0;

    if (shouldRecalc && !this.isMoving) {
      this.recalculatePath(playerRow, playerCol);
    }

    if (!this.isMoving) {
      this.moveToNextWaypoint();
    }
  }

  recalculatePath(playerRow: number, playerCol: number): void {
    this.path = findPath(
      this.maze.cells,
      this.maze.height,
      this.maze.width,
      { row: this.gridRow, col: this.gridCol },
      { row: playerRow, col: playerCol },
    );

    // If no direct path, find the reachable cell closest to the player
    if (this.path.length === 0) {
      this.path = this.findClosestReachablePath(playerRow, playerCol);
    }

    // Skip first waypoint (current position)
    this.pathIndex = this.path.length > 1 ? 1 : 0;
    this.needsRecalc = false;
  }

  /** BFS from current position to find the reachable cell with minimum Manhattan distance to target */
  private findClosestReachablePath(targetRow: number, targetCol: number): GridCell[] {
    const visited = new Set<string>();
    const parent = new Map<string, string | null>();
    const queue: GridCell[] = [{ row: this.gridRow, col: this.gridCol }];
    const startKey = `${this.gridRow},${this.gridCol}`;
    visited.add(startKey);
    parent.set(startKey, null);

    let bestCell: GridCell = { row: this.gridRow, col: this.gridCol };
    let bestDist = Math.abs(this.gridRow - targetRow) + Math.abs(this.gridCol - targetCol);

    const dirs = [
      { wall: Wall.NORTH, dr: -1, dc: 0 },
      { wall: Wall.EAST, dr: 0, dc: 1 },
      { wall: Wall.SOUTH, dr: 1, dc: 0 },
      { wall: Wall.WEST, dr: 0, dc: -1 },
    ];

    while (queue.length > 0) {
      const cell = queue.shift()!;
      const dist = Math.abs(cell.row - targetRow) + Math.abs(cell.col - targetCol);
      if (dist < bestDist) {
        bestDist = dist;
        bestCell = cell;
      }

      for (const { wall, dr, dc } of dirs) {
        if ((this.maze.cells[cell.row][cell.col] & wall) !== 0) continue;
        const nr = cell.row + dr;
        const nc = cell.col + dc;
        if (nr < 0 || nr >= this.maze.height || nc < 0 || nc >= this.maze.width) continue;
        const key = `${nr},${nc}`;
        if (visited.has(key)) continue;
        visited.add(key);
        parent.set(key, `${cell.row},${cell.col}`);
        queue.push({ row: nr, col: nc });
      }
    }

    // Reconstruct path to bestCell
    const path: GridCell[] = [];
    let key: string | null = `${bestCell.row},${bestCell.col}`;
    while (key !== null) {
      const [r, c] = key.split(',').map(Number);
      path.unshift({ row: r, col: c });
      key = parent.get(key) ?? null;
    }
    return path;
  }

  invalidatePath(): void {
    this.needsRecalc = true;
  }

  private moveToNextWaypoint(): void {
    if (this.path.length === 0 || this.pathIndex >= this.path.length) return;

    const next = this.path[this.pathIndex];

    // Verify the step is still passable (wall may have changed)
    const dr = next.row - this.gridRow;
    const dc = next.col - this.gridCol;
    if (Math.abs(dr) + Math.abs(dc) !== 1) {
      // Path is invalid, need recalc
      this.needsRecalc = true;
      return;
    }

    // Check wall in the direction of movement
    let wallFlag = 0;
    if (dr === -1) wallFlag = 1; // NORTH
    if (dr === 1) wallFlag = 4;  // SOUTH
    if (dc === 1) wallFlag = 2;  // EAST
    if (dc === -1) wallFlag = 8; // WEST

    if ((this.maze.cells[this.gridRow][this.gridCol] & wallFlag) !== 0) {
      // Wall appeared, need recalc
      this.needsRecalc = true;
      return;
    }

    const targetPos = this.maze.cellToPixel(next.row, next.col);
    this.isMoving = true;

    this.scene.tweens.add({
      targets: this.sprite,
      x: targetPos.x,
      y: targetPos.y,
      duration: this.tweenDuration,
      ease: 'Linear',
      onComplete: () => {
        this.gridRow = next.row;
        this.gridCol = next.col;
        this.pathIndex++;
        this.isMoving = false;
      },
    });
  }

  destroy(): void {
    this.sprite.destroy();
  }
}
